import { computed, ref, watch } from '@vue/composition-api';
import useTracks from '@/compositions/useTracks';
import isEqual from 'lodash/isEqual';
import useSocket from '@/compositions/useSocket';
import usePublishers from '@/compositions/usePublishers';
import SoundMeter from '@/lib/SoundMeter';
import useErrors from '@/compositions/useErrors';

const streams = ref([]);
const currentStream = ref(null);
const cameraStream = ref(null);
const myStream = ref(null);
const isMyStream = ref(false);
const audioLevel = ref([]);
const currentUserId = ref(null);
const timeout = ref(null);
const otherCapture = ref(false);
const myCapture = ref(false);
const fixed = ref(null);
const error = ref(null);
const cameraDisabled = ref(false);
const microphoneDisabled = ref(false);
const peerConnection = ref(null);

const eventHandlerSet = ref(false);
const publisher = ref(null);

const cameraId = ref(null);
const microphoneId = ref(null);
const tracks = ref([]);
const deletedPublishers = ref([]);

const currentStreamInitCompleted = ref(null);

const waitingPermission = ref(false);

const { networkError } = useErrors();
const networkErrorHappened = ref(0);
const lastMicrophoneDisabledState = ref(false);

watch(networkError, () => {
  if (!networkError.value) {
    return;
  }
  networkErrorHappened.value = 2;
  streams.value = [];
});

export default function useStreams() {
  const { addTrack, stopAllTracks } = useTracks();
  const { registerMessage, sendEvent } = useSocket();

  cameraId.value = localStorage.getItem('camera') || null;
  microphoneId.value = localStorage.getItem('microphone') || null;

  function setMicrophoneDisabled(value) {
    microphoneDisabled.value = value;
  }

  function setCamera(id) {
    cameraId.value = id;
    localStorage.setItem('camera', id);
  }

  function setMicrophone(id) {
    microphoneId.value = id;
    localStorage.setItem('microphone', id);
  }

  const { publishers } = usePublishers();

  async function getCameras() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => {
      if (d.kind !== 'videoinput') {
        return false;
      }
      if (typeof d.getCapabilities !== 'function') {
        return true;
      }
      const { facingMode } = d.getCapabilities();

      if (!facingMode || !Array.isArray(facingMode) || facingMode.length === 0) {
        return true;
      }
      return facingMode.includes('user');
    });
  }

  watch(() => streams.value, (newVal, oldVal) => {
    if (!currentStream.value || isEqual(newVal, oldVal)) {
      return;
    }
    const res = newVal.some((s) => s.stream.id === currentStream.value?.id)
        || currentStream.value.id === myStream.value?.id;
    if (!res) {
      currentStream.value = null;
    }
  });

  function filterStreamsByPublishers(_publishers) {
    streams.value = streams.value.filter((s) => _publishers.some(
      (p) => p.data.janusRoomMemberId === s.janusRoomMemberId,
    ));
  }

  function getShareStream() {
    return streams.value.find((s) => publishers.value.some(
      (p) => p.share && (p.data.janusRoomMemberId === s.janusRoomMemberId),
    ));
  }

  function addStream({ janusRoomMemberId, stream, kind }) {
    const streamFound = streams.value.find((s) => s.janusRoomMemberId === janusRoomMemberId);
    if (streamFound && currentStream.value.id === streamFound.stream.id) {
      if (!currentStreamInitCompleted.value) {
        currentStream.value = stream;
      }
      streams.value = streams.value.filter((s) => s.janusRoomMemberId !== janusRoomMemberId);
    }

    streams.value.push({
      janusRoomMemberId,
      stream,
    });
  }

  function setCurrentStream(stream, _isMyStream = false) {
    currentStream.value = stream;
    isMyStream.value = _isMyStream;
    if (_isMyStream && fixed.value === 'my') {
      fixed.value = null;
    } else if (_isMyStream) {
      fixed.value = 'my';
      currentUserId.value = null;
    }
  }

  function initCurrentStream() {
    watch(streams, () => {
      const shareStream = getShareStream();

      if (currentStreamInitCompleted.value && (!shareStream || currentUserId.value === shareStream.janusRoomMemberId)) {
        return;
      }

      if (shareStream) {
        currentUserId.value = shareStream.janusRoomMemberId;
        setCurrentStream(shareStream.stream);
        otherCapture.value = true;
        currentStreamInitCompleted.value = true;
      } else if (streams.value.length) {
        setCurrentStream(streams.value[0].stream);
        currentUserId.value = streams.value[0].janusRoomMemberId;
        currentStreamInitCompleted.value = true;
      } else if (myStream.value) {
        setCurrentStream(myStream.value);
      }
    });
  }

  function setMyStream(stream) {
    if (myStream.value?.id === currentStream.value?.id) {
      currentStream.value = stream;
      isMyStream.value = true;
    }
    myStream.value = stream;
  }

  function setCurrentStreamMyStream() {
    currentStream.value = myStream.value;
    isMyStream.value = true;
  }

  function storeCameraStream() {
    cameraStream.value = myStream.value;
  }

  function restoreCameraStream() {
    setMyStream(cameraStream.value);
  }

  function changeStreamByMemberId(janusRoomMemberId, fix = false) {
    const streamData = streams.value.find((s) => s.janusRoomMemberId === janusRoomMemberId);

    if (!streamData) {
      return;
    }

    if (fix && fixed.value === janusRoomMemberId) {
      fixed.value = null;
      return;
    }

    currentUserId.value = janusRoomMemberId;
    if (fix) {
      fixed.value = janusRoomMemberId;
    } else {
      fixed.value = null;
    }
    setCurrentStream(streamData.stream);
  }

  if (!eventHandlerSet.value) {
    registerMessage('room:share', ({ state, janusRoomMemberId }) => {
      if (state) {
        changeStreamByMemberId(janusRoomMemberId);
      }

      otherCapture.value = state;
    });
  }
  eventHandlerSet.value = true;

  function setOnEnd(track) {
    if (!track) {
      return;
    }

    const onTrackStateChanged = (state) => {
      if (networkErrorHappened.value > 0) {
        networkErrorHappened.value--;
        return;
      }

      switch (track.kind) {
        case 'audio':
          setMicrophoneDisabled(state);
          sendEvent('action:audio', { state: !microphoneDisabled.value });
          break;
        case 'video':
          if (myCapture.value) {
            return;
          }
          cameraDisabled.value = state;
          sendEvent('action:video', { state: !cameraDisabled.value });
          break;
        default:
      }
    };

    // eslint-disable-next-line no-param-reassign
    track.onended = () => {
      onTrackStateChanged(true);
    };
    // eslint-disable-next-line no-param-reassign
    track.oninactive = () => {
      onTrackStateChanged(true);
    };
    // eslint-disable-next-line no-param-reassign
    track.onmute = () => {
      onTrackStateChanged(true);
    };
    // eslint-disable-next-line no-param-reassign
    track.onunmute = () => {
      onTrackStateChanged(false);
    };
    // eslint-disable-next-line no-param-reassign
    track.onactive = () => {
      onTrackStateChanged(false);
    };
  }

  async function broadcastStream(stream, _peerConnection) {
    peerConnection.value = _peerConnection;
    stream.getTracks().forEach((track) => {
      if (cameraDisabled.value && track.kind === 'video') {
        // eslint-disable-next-line no-param-reassign
        track.enabled = false;
        setTimeout(() => {
          track.stop();
        }, 1000);
      } else if (microphoneDisabled.value && track.kind === 'audio') {
        // eslint-disable-next-line no-param-reassign
        track.enabled = false;
        setTimeout(() => {
          track.stop();
        }, 1000);
      } else {
        setOnEnd(track);
      }
      addTrack(_peerConnection.addTrack(track, stream));
    });
    const offer = await _peerConnection.createOffer({});
    await _peerConnection.setLocalDescription(offer);

    return { type: offer.type, sdp: offer.sdp };
  }

  async function requestMediaStream(_constraints = {}) {
    waitingPermission.value = true;

    const constraints = {
      audio: {},
      video: {
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    };

    const cameras = await getCameras();
    let resultConstraints = Object.assign(constraints, _constraints);

    if (microphoneId.value && resultConstraints.audio) {
      if (resultConstraints.audio === true) {
        resultConstraints.audio = {};
      }
      constraints.audio.deviceId = {
        exact: microphoneId.value,
      };
    }

    if (cameraId.value && resultConstraints.video) {
      if (resultConstraints.video === true) {
        resultConstraints.video = {};
      }
      constraints.video.deviceId = {
        exact: cameraId.value,
      };
    }

    if (!cameras.length) {
      resultConstraints = { ...resultConstraints, video: false };
      cameraDisabled.value = true;
    }

    try {
      const result = await navigator.mediaDevices.getUserMedia(
        resultConstraints,
      );
      error.value = null;

      result.getTracks().forEach((track) => {
        tracks.value.push({
          track,
          data: constraints,
        });
      });

      waitingPermission.value = false;

      return result;
    } catch (e) {
      console.error('requestMediaStream failed:', e);
      waitingPermission.value = false;

      cameraDisabled.value = true;
      setMicrophoneDisabled(true);
      error.value = e;
      throw e;
    }
  }

  function setAudioLevel(janusMemberId, volume) {
    const user = audioLevel.value.find((u) => u.janusMemberId === janusMemberId);
    if (!user) {
      audioLevel.value.push({ janusMemberId, volume });
    } else {
      user.volume = volume;
    }

    if (deletedPublishers.value.length) {
      audioLevel.value = audioLevel.value.filter(
        (p) => !deletedPublishers.value.some((d) => d === p.janusMemberId),
      );
      deletedPublishers.value = [];
    }

    audioLevel.value.sort((a, b) => b.volume - a.volume);
    const maxLevel = audioLevel.value[0];

    if (!maxLevel || maxLevel.volume === 0 || otherCapture.value || myCapture.value || fixed.value) {
      return;
    }

    if (maxLevel.janusMemberId !== currentUserId.value) {
      if (timeout.value) {
        return;
      }
      timeout.value = setTimeout(() => {
        changeStreamByMemberId(maxLevel.janusMemberId);
        timeout.value = null;
      }, 2000);
    } else if (maxLevel.volume > 0) {
      clearTimeout(timeout.value);
      timeout.value = null;
    }
  }

  function deletePublisherFromLevel(janusMemberId) {
    deletedPublishers.value.push(janusMemberId);
    const level = audioLevel.value.find((l) => l.janusMemberId === janusMemberId);

    if (!level) {
      return;
    }

    clearInterval(level?.interval);
    const newLevels = audioLevel.value.filter(
      (p) => p.janusMemberId !== janusMemberId,
    );
    if (newLevels?.length && currentUserId.value === janusMemberId) {
      changeStreamByMemberId(newLevels[0].janusMemberId);
    }

    if (!newLevels?.length) {
      setCurrentStreamMyStream();
    }
  }

  function getLevel(streamData, callback) {
    window.AudioContext = window.AudioContext || window.webkitAudioContext;
    window.audioContext = new AudioContext();

    // eslint-disable-next-line no-param-reassign
    streamData.soundMeter = new SoundMeter(window.audioContext);
    const soundMeter = streamData.soundMeter;
    soundMeter.connectToSource(streamData, (e) => {
      if (e) {
        console.error('SoundMeter connect error:', e);
        return;
      }
      const interval = setInterval(() => {
        callback(soundMeter.instant.toFixed(2), interval);
      }, 200);
    });
  }

  function stopAllStreamTracks() {
    tracks.value.forEach((t) => t.track.stop());
  }

  return {
    streams: computed(() => streams.value),
    currentStream: computed(() => currentStream.value),
    myStream,
    isMyStream,
    setMyStream,
    addStream,
    changeStreamByMemberId,
    setCurrentStream,
    broadcastStream,
    requestMediaStream,
    storeCameraStream,
    restoreCameraStream,
    initCurrentStream,
    filterStreamsByPublishers,
    setAudioLevel,
    currentUserId,
    fixed,
    cameraDisabled,
    microphoneDisabled,
    getCameras,
    error,
    setOnEnd,
    otherCapture,
    myCapture,
    peerConnection,
    setCamera,
    setMicrophone,
    cameraId,
    microphoneId,
    getLevel,
    audioLevel,
    stopAllStreamTracks,
    deletePublisherFromLevel,
    tracks,
    setCurrentStreamMyStream,
    stopAllTracks,
    waitingPermission,
    lastMicrophoneDisabledState,
  };
}
